import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, UserPlus, UserCheck, Heart, MessageCircle, Eye, ShoppingBag, Trash2, CheckSquare, Square, X, Wand2, LogOut, Repeat2 } from "lucide-react";
import { toast } from "sonner";
import PlyxTagline from "@/components/plyx/PlyxTagline";
import PlyxMovingTagline from "@/components/plyx/PlyxMovingTagline";

const fmt = (n) => {
  if (!n) return "0";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
};

const PlyxProfile = () => {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user: me, logout, loading: authLoading } = usePlyxAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  // iter-119m: profile tab — "videos" (creator's uploads) vs
  // "reposts" (videos this user has reposted from other creators).
  const [tab, setTab] = useState("videos");

  // Guests landing on a creator profile (often via a shared link such as
  // /plyx/u/tobest) get routed to register so they don't bounce off into
  // a partial profile experience they can't actually engage with.
  useEffect(() => {
    if (authLoading) return;
    if (!me) {
      navigate("/plyx/register", {
        replace: true,
        state: { from: `/plyx/u/${handle}` },
      });
    }
  }, [authLoading, me, handle, navigate]);

  useEffect(() => {
    if (authLoading || !me) return;
    setLoading(true);
    plyxApi.get(`/plyx/u/${handle}`)
      .then((r) => {
        setData(r.data);
        setFollowing(r.data.i_follow);
      })
      .catch(() => toast.error("Profile not found"))
      .finally(() => setLoading(false));
  }, [handle, authLoading, me]);

  const toggleFollow = async () => {
    if (!me) { navigate("/plyx/login"); return; }
    const next = !following;
    setFollowing(next);
    try {
      if (next) await plyxApi.post(`/plyx/u/${handle}/follow`);
      else await plyxApi.delete(`/plyx/u/${handle}/follow`);
    } catch {
      setFollowing(!next);
      toast.error("Could not update follow");
    }
  };

  const [deletingId, setDeletingId] = useState(null);
  const [manageMode, setManageMode] = useState(false);
  const [rebuildingBrand, setRebuildingBrand] = useState(false);

  const handleRebuildBrand = async () => {
    if (rebuildingBrand) return;
    setRebuildingBrand(true);
    try {
      const r = await plyxApi.post("/plyx/me/videos/rebuild-brand");
      const dead = r.data.unrecoverable_video_ids || [];
      if (dead.length > 0) {
        // Surface the dead clips as the manage-mode selection so the
        // creator can one-tap delete them. Trying Re-process N times
        // on the SAME corrupted bytes will never succeed — these clips
        // physically can't be repaired without re-uploading from the
        // original phone file. Make that very clear and offer the
        // delete action immediately.
        setManageMode(true);
        setSelected(new Set(dead));
        toast(
          `${dead.length} clip${dead.length === 1 ? "" : "s"} can't be recovered. Tap "Delete selected" → re-upload from your phone.`,
          {
            duration: 10000,
            description: r.data.scheduled > 0
              ? `${r.data.scheduled} other clip${r.data.scheduled === 1 ? "" : "s"} are re-processing now.`
              : undefined,
          },
        );
      } else if (r.data.scheduled > 0) {
        toast.success(r.data.message || `Re-processing ${r.data.scheduled} videos`, { duration: 8000 });
      } else {
        toast.message("Nothing to re-process — your videos look clean.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start re-processing");
    } finally {
      setRebuildingBrand(false);
    }
  };
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null); // { broken_count, broken: [{ video_id, reasons[] }], total, ok }

  const scanForBroken = async (opts = {}) => {
    const { silent = false } = opts;
    if (scanning) return;
    setScanning(true);
    try {
      const r = await plyxApi.get("/plyx/me/videos/health");
      setScanResult(r.data);
      // Auto-enter manage mode + pre-select all broken ids so the
      // creator can review the picks and one-tap delete.
      if (r.data.broken_count > 0) {
        setManageMode(true);
        setSelected(new Set(r.data.broken.map((b) => b.video_id)));
        // Friendlier wording — broken videos can often be REPROCESSED
        // (re-run server-side branding from the original) before the
        // creator has to actually delete anything. Don't push them to
        // delete first.
        if (!silent) {
          toast.message(`${r.data.broken_count} video${r.data.broken_count === 1 ? "" : "s"} need attention — try Re-process first.`, { duration: 6000 });
        } else {
          toast.message(`Heads-up: ${r.data.broken_count} video${r.data.broken_count === 1 ? "" : "s"} look black or unplayable. Tap Re-process to fix.`, { duration: 6000 });
        }
      } else if (!silent) {
        toast.success("All your videos look healthy.");
      }
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Could not scan videos");
    } finally {
      setScanning(false);
    }
  };

  // Once per session per handle: auto-run the health scan as soon as
  // we land on our own profile, so broken/black-tile uploads surface
  // without the creator having to remember to tap "Scan broken".
  // Silent — no toast on a healthy account, only a soft heads-up when
  // something is actually wrong.
  const isMe = data?.user && me && me.plyx_user_id === data.user.plyx_user_id;
  useEffect(() => {
    if (!isMe || !data || (data.videos?.length || 0) === 0) return;
    const key = `plyx_auto_scan:${data.user.handle}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* private mode — fall through, will re-scan next mount */ }
    scanForBroken({ silent: true });
  }, [isMe, data?.user?.handle, data?.videos?.length]);

  const toggleSelected = (videoId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(videoId)) next.delete(videoId);
      else next.add(videoId);
      return next;
    });
  };

  const exitManageMode = () => {
    setManageMode(false);
    setSelected(new Set());
  };

  const handleDeleteVideo = async (videoId, caption) => {
    if (deletingId) return;
    const preview = (caption || "").trim().slice(0, 40) || "this video";
    if (!window.confirm(`Delete "${preview}"? This can't be undone.`)) return;
    setDeletingId(videoId);
    try {
      await plyxApi.delete(`/plyx/videos/${videoId}`);
      setData((d) => d ? {
        ...d,
        user: { ...d.user, video_count: Math.max(0, (d.user.video_count || 1) - 1) },
        videos: d.videos.filter((v) => v.video_id !== videoId),
      } : d);
      toast.success("Video removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete video");
    } finally {
      setDeletingId(null);
    }
  };

  const handleBulkDelete = async () => {
    if (bulkDeleting || selected.size === 0) return;
    const ids = Array.from(selected);
    if (!window.confirm(`Delete ${ids.length} video${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    setBulkDeleting(true);
    // Parallel deletes — typical use case is cleaning up 5–20 broken
    // uploads at once, so we don't bother with batching/throttling.
    const results = await Promise.allSettled(
      ids.map((id) => plyxApi.delete(`/plyx/videos/${id}`))
    );
    const okIds = ids.filter((_, i) => results[i].status === "fulfilled");
    const failedCount = ids.length - okIds.length;
    setData((d) => d ? {
      ...d,
      user: { ...d.user, video_count: Math.max(0, (d.user.video_count || 0) - okIds.length) },
      videos: d.videos.filter((v) => !okIds.includes(v.video_id)),
    } : d);
    setSelected(new Set());
    setBulkDeleting(false);
    if (okIds.length > 0) {
      toast.success(`Removed ${okIds.length} video${okIds.length === 1 ? "" : "s"}`);
    }
    if (failedCount > 0) {
      toast.error(`${failedCount} could not be deleted. Try again.`);
    }
    if (okIds.length === ids.length) {
      // All cleaned up — drop out of manage mode automatically.
      setManageMode(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  }
  if (!data) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">Profile not found</div>;
  }

  const u = data.user;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid={`plyx-profile-${u.handle}`}>
      <div className="container-narrow py-10">
        <div className="flex items-start gap-5 sm:gap-8 flex-wrap">
          <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-white/10 overflow-hidden flex items-center justify-center text-3xl font-display">
            {u.avatar_url
              ? <img src={plyxMediaUrl(u.avatar_url)} alt={u.handle} className="w-full h-full object-cover"/>
              : u.display_name?.slice(0, 1) || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight">@{u.handle}</h1>
            <div className="text-white/70 mt-0.5">{u.display_name}</div>
            <div className="flex gap-5 mt-3 text-sm">
              <Link
                to={`/plyx/u/${u.handle}/network?tab=followers`}
                data-testid="plyx-profile-follower-count"
                className="hover:text-amber-300 transition-colors"
              ><strong>{fmt(u.follower_count)}</strong> <span className="text-white/50">followers</span></Link>
              <Link
                to={`/plyx/u/${u.handle}/network?tab=following`}
                data-testid="plyx-profile-following-count"
                className="hover:text-amber-300 transition-colors"
              ><strong>{fmt(u.following_count)}</strong> <span className="text-white/50">following</span></Link>
              <div data-testid="plyx-profile-video-count"><strong>{fmt(u.video_count)}</strong> <span className="text-white/50">videos</span></div>
            </div>
            {u.bio && <p className="mt-3 text-white/80 max-w-md whitespace-pre-wrap">{u.bio}</p>}
            {u.country && <div className="mt-1 text-xs text-white/50">{u.country}</div>}
            {!isMe ? (
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={toggleFollow}
                  data-testid="plyx-profile-follow-btn"
                  className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                    following ? "bg-white/15 text-white hover:bg-white/25" : "text-[#0A0A0A]"
                  }`}
                  style={following ? {} : { background: "#E85D4E" }}
                >
                  {following ? <><UserCheck className="w-4 h-4"/> Following</> : <><UserPlus className="w-4 h-4"/> Follow</>}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const r = await plyxApi.post("/plyx/dm/conversations", { to_handle: u.handle });
                      window.location.href = `/plyx/dm/${r.data.conversation_id}`;
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Could not open chat");
                    }
                  }}
                  data-testid="plyx-profile-message-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> Message
                </button>
              </div>
            ) : (
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to="/plyx/upload"
                  data-testid="plyx-profile-upload-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-[#0A0A0A]"
                  style={{ background: "#E85D4E" }}
                >
                  Upload a video
                </Link>
                <Link
                  to="/plyx/settings"
                  data-testid="plyx-profile-edit-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold border border-white/20 hover:bg-white/10"
                >
                  Edit profile
                </Link>
                <Link
                  to="/plyx/wallet"
                  data-testid="plyx-profile-wallet-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold border border-white/20 hover:bg-white/10"
                >
                  Wallet
                </Link>
                <Link
                  to="/plyx/shop/manage"
                  data-testid="plyx-profile-shop-manage-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold border border-[#FFB42D]/40 text-[#FFB42D] hover:bg-[#FFB42D]/10"
                >
                  Manage Shop
                </Link>
                <Link
                  to="/plyx/insights"
                  data-testid="plyx-profile-insights-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold border border-[#00E0C7]/40 text-white/85 hover:bg-white/10/10"
                >
                  AI Insights
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm("Log out of Plyx?")) {
                      logout();
                      navigate("/plyx");
                    }
                  }}
                  data-testid="plyx-profile-logout-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold border border-white/20 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                >
                  <LogOut className="w-4 h-4" /> Log out
                </button>
              </div>
            )}
            {/* Always show "View Shop" for any profile if products > 0 (or just always — shop page handles empty) */}
            {!isMe && (
              <Link
                to={`/plyx/u/${handle}/shop`}
                data-testid="plyx-profile-shop-view-btn"
                className="mt-2 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold border border-[#FFB42D]/40 text-[#FFB42D] hover:bg-[#FFB42D]/10"
              >
                <ShoppingBag className="w-3.5 h-3.5"/> Visit @{handle}&apos;s shop
              </Link>
            )}
          </div>
        </div>

        {/* Manage / select toolbar (own profile only) */}
        {isMe && data.videos.length > 0 && (
          <div className="mt-10 flex items-center justify-between gap-3 flex-wrap" data-testid="plyx-profile-manage-bar">
            <div className="text-sm text-white/60">
              {manageMode
                ? <>Tap tiles to select. <span className="text-white font-semibold">{selected.size}</span> selected.</>
                : <>Tap a tile to view it, or enter manage mode to bulk-delete.</>}
            </div>
            <div className="flex items-center gap-2">
              {manageMode ? (
                <>
                  <button
                    type="button"
                    onClick={handleRebuildBrand}
                    disabled={rebuildingBrand}
                    data-testid="plyx-profile-rebuild-brand"
                    title="Re-run Plyx branding from the original source — recovers black videos without deleting"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-300 text-black hover:bg-amber-200 disabled:opacity-40"
                  >
                    {rebuildingBrand
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Re-processing…</>
                      : <><Wand2 className="w-3.5 h-3.5"/> Re-process</>}
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting || selected.size === 0}
                    data-testid="plyx-profile-bulk-delete-btn"
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-white hover:bg-[#ff5598] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {bulkDeleting
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Deleting…</>
                      : <><Trash2 className="w-3.5 h-3.5"/> Delete selected ({selected.size})</>}
                  </button>
                  <button
                    type="button"
                    onClick={exitManageMode}
                    disabled={bulkDeleting}
                    data-testid="plyx-profile-manage-exit"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-white/20 hover:bg-white/10 disabled:opacity-40"
                  >
                    <X className="w-3.5 h-3.5"/> Done
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={scanForBroken}
                    disabled={scanning}
                    data-testid="plyx-profile-scan-broken"
                    title="Scan your account for unplayable / black-tile videos"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#FFB42D]/40 text-[#FFB42D] hover:bg-[#FFB42D]/10 disabled:opacity-50"
                  >
                    {scanning
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Scanning…</>
                      : <><Wand2 className="w-3.5 h-3.5"/> Scan broken</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => setManageMode(true)}
                    data-testid="plyx-profile-manage-enter"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-white/20 hover:bg-white/10"
                  >
                    <CheckSquare className="w-3.5 h-3.5"/> Select
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Scan result chip — visible after a "Scan broken" run so the
            creator can see WHY each video was flagged before they
            blindly accept the auto-selection. */}
        {isMe && scanResult && (
          <div
            data-testid="plyx-profile-scan-summary"
            className={`mt-3 rounded-xl border px-4 py-3 text-xs ${
              scanResult.broken_count > 0
                ? "border-[#FF2D7A]/40 bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7]/5 text-white/85"
                : "border-emerald-400/30 bg-emerald-400/5 text-emerald-200"
            }`}
          >
            <div className="font-semibold mb-1">
              {scanResult.broken_count > 0
                ? `${scanResult.broken_count} of ${scanResult.total} videos look broken`
                : `All ${scanResult.total} videos look healthy 🎉`}
            </div>
            {scanResult.broken_count > 0 && (
              <ul className="space-y-1 max-h-32 overflow-auto pr-1">
                {scanResult.broken.slice(0, 6).map((b) => (
                  <li key={b.video_id} className="text-white/70">
                    <span className="text-white/95">
                      {b.caption ? `"${b.caption.slice(0, 40)}"` : `(${b.media_type})`}
                    </span>
                    <span className="text-white/50"> — {b.reasons.join("; ")}</span>
                  </li>
                ))}
                {scanResult.broken.length > 6 && (
                  <li className="text-white/40">…and {scanResult.broken.length - 6} more</li>
                )}
              </ul>
            )}
          </div>
        )}

        {/* Video grid */}
        {/* Animated brand tagline — sits above the gallery so the
            profile inherits the Plyx voice even when viewed standalone. */}
        <div className="mt-6 mb-2 flex items-center justify-center">
          <PlyxTagline variant="gallery" />
        </div>
        {/* Letter-by-letter marquee under the static tagline — adds the
            kinetic "where moments become movements" travelling ribbon
            the user asked for. */}
        <PlyxMovingTagline variant="gallery" className="mb-3" />

        {/* iter-119m: Videos / Reposts tab switcher. Reposts (videos
            this user has lifted from other creators) only render when
            the array is non-empty — keeps the profile uncluttered for
            creators who don't repost. */}
        <div
          className="flex items-center justify-center gap-1 mb-4 sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-10 py-2 border-b border-white/5"
          data-testid="plyx-profile-tabs"
        >
          <button
            type="button"
            onClick={() => setTab("videos")}
            data-testid="plyx-profile-tab-videos"
            className={
              "px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors "
              + (tab === "videos" ? "bg-white text-black" : "text-white/60 hover:text-white")
            }
          >
            Videos · {fmt(data.videos.length)}
          </button>
          <button
            type="button"
            onClick={() => setTab("reposts")}
            data-testid="plyx-profile-tab-reposts"
            className={
              "px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 transition-colors "
              + (tab === "reposts" ? "bg-white/10 text-black" : "text-white/60 hover:text-white")
            }
          >
            <Repeat2 className="w-3 h-3" /> Reposts · {fmt((data.reposts || []).length)}
          </button>
        </div>

        {(() => {
          const tiles = tab === "reposts" ? (data.reposts || []) : data.videos;
          return (
        <div className={`${isMe && tiles.length > 0 ? "mt-3" : "mt-4"} grid grid-cols-3 sm:grid-cols-4 gap-1 sm:gap-2`} data-testid={`plyx-profile-grid-${tab}`}>
          {tiles.length === 0 && (
            <div className="col-span-full text-center text-white/50 py-16">
              {tab === "reposts"
                ? (isMe
                    ? "You haven't reposted anyone's video yet. Tap the 🔁 button on any video to share it here."
                    : "No reposts yet.")
                : "No videos yet."}
            </div>
          )}
          {tiles.map((v) => {
            const isSelected = selected.has(v.video_id);
            const tileBody = (
              <>
                {v.thumbnail_url ? (
                  <img src={plyxMediaUrl(v.thumbnail_url)} alt="" className="w-full h-full object-cover"/>
                ) : (
                  <video src={plyxMediaUrl(v.video_url)} className="w-full h-full object-cover" muted playsInline preload="metadata"/>
                )}
                <div className="absolute bottom-1.5 left-2 right-2 flex items-center justify-between text-xs text-white">
                  <span className="inline-flex items-center gap-1 bg-black/50 backdrop-blur px-1.5 py-0.5 rounded">
                    <Eye className="w-3 h-3"/> {fmt(v.view_count)}
                  </span>
                  <span className="inline-flex items-center gap-1 bg-black/50 backdrop-blur px-1.5 py-0.5 rounded">
                    <Heart className="w-3 h-3"/> {fmt(v.like_count)}
                  </span>
                </div>
              </>
            );
            return (
              <div
                key={v.video_id}
                className={`relative aspect-[9/16] bg-black rounded-md overflow-hidden group ${
                  isSelected ? "ring-2 ring-[#FF2D7A] ring-offset-1 ring-offset-[#0A0A0A]" : ""
                }`}
              >
                {manageMode && isMe ? (
                  <button
                    type="button"
                    onClick={() => toggleSelected(v.video_id)}
                    data-testid={`plyx-profile-select-${v.video_id}`}
                    className="absolute inset-0 z-10 cursor-pointer"
                    aria-label={isSelected ? "Deselect video" : "Select video"}
                    aria-pressed={isSelected}
                  >
                    {tileBody}
                    <span
                      className={`absolute top-1.5 left-1.5 inline-flex items-center justify-center w-7 h-7 rounded-full backdrop-blur transition-colors ${
                        isSelected
                          ? "bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-white"
                          : "bg-black/70 text-white/80"
                      }`}
                    >
                      {isSelected
                        ? <CheckSquare className="w-3.5 h-3.5"/>
                        : <Square className="w-3.5 h-3.5"/>}
                    </span>
                  </button>
                ) : (
                  <Link
                    to={`/plyx/v/${v.video_id}`}
                    data-testid={`plyx-profile-video-${v.video_id}`}
                    className="absolute inset-0"
                  >
                    {tileBody}
                  </Link>
                )}
                {isMe && !manageMode && (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteVideo(v.video_id, v.caption); }}
                    disabled={deletingId === v.video_id}
                    data-testid={`plyx-profile-delete-${v.video_id}`}
                    title="Delete this video"
                    aria-label="Delete this video"
                    className="absolute top-1.5 right-1.5 z-10 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/70 backdrop-blur text-white/85 hover:bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] hover:text-white transition-colors disabled:opacity-60"
                  >
                    {deletingId === v.video_id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin"/>
                      : <Trash2 className="w-3.5 h-3.5"/>}
                  </button>
                )}
                {tab === "reposts" && (
                  <span
                    className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/10/90 backdrop-blur text-[10px] font-bold text-black"
                    title="Reposted"
                  >
                    <Repeat2 className="w-3 h-3" /> Reposted
                  </span>
                )}
              </div>
            );
          })}
        </div>
          );
        })()}
      </div>
    </div>
  );
};

export default PlyxProfile;
